import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { DownloadCloud02Icon } from '@/components/ui/icons'
import type { HostReviewContext } from '@/features/host-review/types'
import {
	getReviewCountdown,
	isReviewOverdue
} from '@/features/host-review/utils'
import {
	PhotosetFinal,
	PhotosetFinalBadge
} from '@/features/photoset/components/photoset-final'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

/**
 * The waiting page for a photoset whose host review is open, shown identically
 * on the editing and QA tracks.
 *
 * Structure and copy are the existing `host_selection.pending` screen - the one
 * operators already know - kept deliberately, so nothing about the screen
 * changes when a photoset starts being served by the host review contract
 * instead of the older host-selection fields. What changes is the source: the
 * deadline is the review's own, not `review_assets_completed`.
 *
 * The disabled download is part of that copy: it says plainly that nothing is
 * downloadable yet. Beyond it the page carries no operator action, and that
 * absence is the contract rather than an omission - a review's four exits
 * belong to the host, to the expiry sweep, or to operations acting through the
 * close operation, never to an editing-track shortcut.
 */
export const HostReviewWaiting = ({
	review,
	isLoading
}: {
	review?: HostReviewContext | null
	isLoading?: boolean
}) => {
	const { t } = useTranslation()

	if (isLoading) {
		return (
			<PhotosetFinal
				isLoading
				subtitle=''
				title={t('photoset:host_selection.pending.title')}
			/>
		)
	}

	// The state says a review is open but its context is missing. The deadline is
	// the only thing that needs it, so the screen still says the true and useful
	// part rather than spinning forever on data that has already arrived.
	if (!review) {
		return (
			<PhotosetFinal
				endSlot={
					<div className='flex flex-col items-center gap-3'>
						<ButtonLegacy disabled size='md' type='button'>
							<DownloadCloud02Icon />
							{t('photoset:host_selection.pending.unavailable')}
						</ButtonLegacy>
						<ButtonLinkLegacy
							size='sm'
							to={routes.WELCOME.path}
							type='button'
							variant='tertiary'
						>
							{t('photoset:host_selection.back_home')}
						</ButtonLinkLegacy>
					</div>
				}
				status='pending'
				subtitle={t('host_review:waiting.subtitle')}
				title={t('photoset:host_selection.pending.title')}
			/>
		)
	}

	// Past the deadline the sweep has not run yet: the set is still formally in
	// review, so promising a countdown that reads "1min" forever would be worse
	// than saying plainly that it closes shortly.
	const isOverdue = isReviewOverdue(review.deadline)

	return (
		<PhotosetFinal
			badge={
				<PhotosetFinalBadge tone={isOverdue ? 'neutral' : 'warning'}>
					{isOverdue
						? t('host_review:waiting.overdue')
						: t('photoset:host_selection.pending.window', {
								period: getReviewCountdown(review.deadline)
							})}
				</PhotosetFinalBadge>
			}
			endSlot={
				<div className='flex flex-col items-center gap-3'>
					<ButtonLegacy disabled size='md' type='button'>
						<DownloadCloud02Icon />
						{t('photoset:host_selection.pending.unavailable')}
					</ButtonLegacy>
					<ButtonLinkLegacy
						size='sm'
						to={routes.WELCOME.path}
						type='button'
						variant='tertiary'
					>
						{t('photoset:host_selection.back_home')}
					</ButtonLinkLegacy>
				</div>
			}
			status='pending'
			subtitle={t('host_review:waiting.subtitle')}
			title={t('photoset:host_selection.pending.title')}
		/>
	)
}

HostReviewWaiting.displayName = 'HostReviewWaiting'
